import * as React from 'react'

import { AdditionalCompensationFormFieldsFragment } from 'generated/resources/graphql'
import {
  ContractFlowCustomFieldFragment,
  ContractFlowCustomFieldValueFragment,
} from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import type Compensation from 'modules/contracts/store/models/Compensation'
import type ContractVersionPayrollPolicy from 'modules/contracts/store/models/ContractVersionPayrollPolicy'
import type JobLevelSalaryRange from 'modules/job_catalog/store/models/JobLevelSalaryRange'

import { LegalEntityContractData } from './legalEntityContractDataContext'

interface Props {
  salaryRanges: JobLevelSalaryRange[]
  contractVersionPayrollPolicy: ContractVersionPayrollPolicy | null
  legalEntityContractData: LegalEntityContractData
}

type BaseAdditionalCompensation = Omit<AdditionalCompensationFormFieldsFragment, 'id'>

type AdditionalCompensationIdentifier =
  | {
      id: string
      tmpID?: string
    }
  | {
      id?: string
      tmpID: string
    }

export type InMemoryAdditionalCompensation = BaseAdditionalCompensation &
  AdditionalCompensationIdentifier

export interface ContractFlow {
  customFields: ContractFlowCustomFieldFragment[] | null
  customValues: ContractFlowCustomFieldValueFragment[] | null
  compensations: Compensation[] | null
  additionalCompensations: InMemoryAdditionalCompensation[] | null
  initialCompensations: Compensation[] | null
  initialAdditionalCompensations: InMemoryAdditionalCompensation[] | null
  salaryRanges: JobLevelSalaryRange[]
  contractVersionPolicy: ContractVersionPayrollPolicy | null
  legalEntityContractData: LegalEntityContractData
  setCustomFields: (customFields: ContractFlowCustomFieldFragment[]) => void
  setCustomValues: (customFields: ContractFlowCustomFieldValueFragment[]) => void
  setCompensations: (compensations: Compensation[]) => void
  setAdditionalCompensations: (additionalCompensations: InMemoryAdditionalCompensation[]) => void
  setInitialCompensations: (compensations: Compensation[]) => void
  setInitialAdditionalCompensations: (
    additionalCompensations: InMemoryAdditionalCompensation[]
  ) => void
  setContractVersionPolicy: (contractVersionPolicies: ContractVersionPayrollPolicy | null) => void
}

const Context = React.createContext<ContractFlow | null>(null)

export const ContractFlowProvider = observer(
  ({
    salaryRanges,
    contractVersionPayrollPolicy,
    legalEntityContractData,
    children,
  }: React.PropsWithChildren<Props>) => {
    const [customFields, setCustomFields] = React.useState<
      ContractFlowCustomFieldFragment[] | null
    >(null)
    const [customValues, setCustomValues] = React.useState<
      ContractFlowCustomFieldValueFragment[] | null
    >(null)
    const [compensations, setCompensations] = React.useState<Compensation[] | null>(null)
    const [additionalCompensations, setAdditionalCompensations] = React.useState<
      InMemoryAdditionalCompensation[] | null
    >(null)
    const [initialCompensations, setInitialCompensations] = React.useState<Compensation[] | null>(
      null
    )
    const [initialAdditionalCompensations, setInitialAdditionalCompensations] = React.useState<
      InMemoryAdditionalCompensation[] | null
    >(null)
    const [contractVersionPolicy, setContractVersionPolicy] =
      React.useState<ContractVersionPayrollPolicy | null>(contractVersionPayrollPolicy)

    const visibleCustomFields =
      customFields
        ?.filter((field: ContractFlowCustomFieldFragment) => field.visible)
        .sort((a, b) => {
          if (!a.position) return 1
          if (!b.position) return -1

          return a.position - b.position
        }) ?? null

    const contractFlowData = {
      customFields: visibleCustomFields,
      customValues,
      compensations,
      additionalCompensations,
      initialCompensations,
      initialAdditionalCompensations,
      salaryRanges,
      contractVersionPolicy,
      legalEntityContractData,
      setCustomFields,
      setCustomValues,
      setCompensations,
      setInitialCompensations,
      setInitialAdditionalCompensations,
      setAdditionalCompensations,
      setContractVersionPolicy,
    }

    return <Context.Provider value={contractFlowData}>{children}</Context.Provider>
  }
)

export const useContractFlow = (): ContractFlow => {
  const contractFlow = React.useContext(Context)

  if (!contractFlow) {
    throw new Error('useContractFlow must be used within a ContractFlowProvider')
  }

  return contractFlow
}
